<template>
  <div class="h-screen w-full bg-white flex flex-col">
    <!-- Nav -->
    <div class="flex-shrink-0 h-14">
      <nav-menu />
    </div>

    <!-- Toolbar -->
    <div class="flex-shrink-0 bg-gray-900 text-white px-6 py-3">
      <div class="container mx-auto flex items-center flex-wrap gap-4">
        <!-- Date Picker -->
        <div class="flex items-center">
          <label class="text-xs text-gray-400 mr-2">Date</label>
          <flat-pickr
            class="bg-gray-800 text-white rounded px-3 py-2 text-sm w-40 outline-none"
            :config="dateConfig"
            v-model="selectedDate"
          ></flat-pickr>
        </div>

        <!-- Date Range for Export -->
        <div class="flex items-center">
          <label class="text-xs text-gray-400 mr-2">Export Range</label>
          <flat-pickr
            class="bg-gray-800 text-white rounded px-3 py-2 text-sm w-56 outline-none"
            :config="rangeConfig"
            v-model="exportRange"
          ></flat-pickr>
        </div>

        <!-- Export CSV Button -->
        <button
          @click="exportCSV"
          :disabled="!exportRange || exporting"
          :class="[
            'px-4 py-2 rounded text-sm font-medium flex items-center',
            !exportRange || exporting
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          ]"
        >
          <i :class="['fa-solid mr-2', exporting ? 'fa-spinner fa-spin' : 'fa-file-csv']"></i>
          {{ exporting ? 'Exporting...' : 'Export CSV' }}
        </button>

        <div class="flex-grow"></div>

        <div v-if="timesheets.length" class="text-xs text-gray-400">
          {{ timesheets.length }} tester{{ timesheets.length !== 1 ? 's' : '' }}
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-grow overflow-auto">
      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center h-64">
        <div class="text-center">
          <i class="fa-solid fa-spinner fa-spin text-3xl text-primary mb-3"></i>
          <div class="text-gray-500 text-sm">Loading timesheets...</div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!timesheets.length" class="flex items-center justify-center h-64">
        <div class="text-center">
          <i class="fa-solid fa-clock text-5xl text-gray-300 mb-3"></i>
          <div class="text-gray-500">No timesheets found for this date</div>
          <div class="text-gray-400 text-sm mt-1">Timesheets are created when you save a plan in the AI Planner</div>
        </div>
      </div>

      <!-- Table -->
      <div v-else class="container mx-auto py-4 px-6">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
              <th class="py-3 px-3">Tester</th>
              <th class="py-3 px-3">Start Time</th>
              <th class="py-3 px-3">Start Override</th>
              <th class="py-3 px-3">Finish Time</th>
              <th class="py-3 px-3">Total Hours</th>
              <th class="py-3 px-3">Jobs</th>
              <th class="py-3 px-3">Late</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="ts in timesheets"
              :key="ts.id"
              class="border-b border-gray-100 hover:bg-gray-50"
            >
              <td class="py-3 px-3 font-medium text-gray-900">{{ ts.contractorName }}</td>
              <td class="py-3 px-3 text-gray-700 font-mono">{{ ts.scheduledStart }}</td>
              <td class="py-3 px-3">
                <div v-if="editingOverride === ts.id" class="flex items-center gap-2">
                  <input
                    type="time"
                    v-model="overrideValue"
                    class="border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:border-primary"
                  />
                  <button @click="saveOverride(ts)" class="text-primary hover:text-blue-700 text-xs font-medium">Save</button>
                  <button @click="editingOverride = null" class="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                </div>
                <div v-else class="flex items-center gap-2">
                  <span v-if="ts.startOverride" class="font-mono text-primary font-medium">{{ ts.startOverride }}</span>
                  <span v-else class="text-gray-400">-</span>
                  <button
                    @click="startEditing(ts)"
                    class="text-gray-400 hover:text-primary text-xs"
                    title="Set override"
                  >
                    <i class="fa-solid fa-pencil"></i>
                  </button>
                  <button
                    v-if="ts.startOverride"
                    @click="clearOverride(ts)"
                    class="text-gray-400 hover:text-red-500 text-xs"
                    title="Clear override"
                  >
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </td>
              <td class="py-3 px-3 text-gray-700 font-mono">{{ ts.scheduledFinish }}</td>
              <td class="py-3 px-3 text-gray-700">
                {{ computeTotalHours(ts) }}h
              </td>
              <td class="py-3 px-3 text-gray-700">{{ ts.jobCount }}</td>
              <td class="py-3 px-3">
                <span
                  v-if="isLate(ts)"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium"
                >
                  LATE
                </span>
                <span v-else class="text-gray-400">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import NavMenu from "@/components/headers/navMenu";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

import {
  getTimesheetsByDate,
  getTimesheetsByDateRange,
  setFire
} from "../firestore/utils";

export default {
  components: { NavMenu, flatPickr },

  data() {
    return {
      selectedDate: null,
      dateConfig: { enableTime: false, altInput: true, allowInput: false, dateFormat: 'Y-m-d' },
      exportRange: null,
      rangeConfig: { enableTime: false, altInput: true, allowInput: false, dateFormat: 'Y-m-d', mode: 'range' },
      timesheets: [],
      loading: false,
      exporting: false,
      editingOverride: null,
      overrideValue: '',
    };
  },

  watch: {
    selectedDate() {
      this.loadTimesheets();
    }
  },

  methods: {
    async loadTimesheets() {
      if (!this.selectedDate) return;
      this.loading = true;
      try {
        this.timesheets = await getTimesheetsByDate(this.selectedDate);
      } catch (e) {
        console.error('Error loading timesheets:', e);
        this.timesheets = [];
      }
      this.loading = false;
    },

    computeTotalHours(ts) {
      if (ts.scheduledStartMs && ts.scheduledFinishMs) {
        return Math.round((ts.scheduledFinishMs - ts.scheduledStartMs) / 3600000 * 10) / 10;
      }
      return '-';
    },

    isLate(ts) {
      if (!ts.startOverride || !ts.startOverrideMs || !ts.scheduledStartMs) return false;
      return ts.scheduledStartMs > ts.startOverrideMs;
    },

    startEditing(ts) {
      this.editingOverride = ts.id;
      this.overrideValue = ts.startOverride || ts.scheduledStart || '';
    },

    async saveOverride(ts) {
      if (!this.overrideValue) return;
      try {
        // Parse the override time into ms for the same date
        const [hours, minutes] = this.overrideValue.split(':').map(Number);
        const overrideDate = new Date(ts.date + 'T00:00:00');
        overrideDate.setHours(hours, minutes, 0, 0);
        const overrideMs = overrideDate.getTime();

        await setFire({
          collection: 'timesheets',
          doc: ts.id,
          body: {
            startOverride: this.overrideValue,
            startOverrideMs: overrideMs,
            isLate: ts.scheduledStartMs > overrideMs
          }
        });

        // Update local state
        ts.startOverride = this.overrideValue;
        ts.startOverrideMs = overrideMs;
        ts.isLate = ts.scheduledStartMs > overrideMs;

        this.editingOverride = null;
      } catch (e) {
        console.error('Error saving override:', e);
        alert('Failed to save override: ' + e.message);
      }
    },

    async clearOverride(ts) {
      try {
        await setFire({
          collection: 'timesheets',
          doc: ts.id,
          body: {
            startOverride: null,
            startOverrideMs: null,
            isLate: false
          }
        });

        ts.startOverride = null;
        ts.startOverrideMs = null;
        ts.isLate = false;
      } catch (e) {
        console.error('Error clearing override:', e);
      }
    },

    async exportCSV() {
      if (!this.exportRange) return;
      this.exporting = true;

      try {
        // flatpickr range mode returns "YYYY-MM-DD to YYYY-MM-DD"
        const parts = this.exportRange.split(' to ');
        const dateFrom = parts[0];
        const dateTo = parts[1] || parts[0];

        const data = await getTimesheetsByDateRange(dateFrom, dateTo);
        if (!data.length) {
          alert('No timesheets found for this date range.');
          this.exporting = false;
          return;
        }

        // Build CSV
        const headers = ['Date', 'Tester', 'Scheduled Start', 'Start Override', 'Scheduled Finish', 'Total Hours', 'Jobs', 'On-Site (hrs)', 'Drive (hrs)', 'Late'];
        const rows = data.map(ts => {
          const totalHours = ts.scheduledStartMs && ts.scheduledFinishMs
            ? Math.round((ts.scheduledFinishMs - ts.scheduledStartMs) / 3600000 * 10) / 10
            : '';
          const onSiteHrs = ts.totalOnSiteSeconds ? Math.round(ts.totalOnSiteSeconds / 3600 * 10) / 10 : '';
          const driveHrs = ts.totalDriveSeconds ? Math.round(ts.totalDriveSeconds / 3600 * 10) / 10 : '';
          const late = ts.startOverride && ts.startOverrideMs && ts.scheduledStartMs && ts.scheduledStartMs > ts.startOverrideMs ? 'YES' : '';
          return [
            ts.date,
            ts.contractorName,
            ts.scheduledStart,
            ts.startOverride || '',
            ts.scheduledFinish,
            totalHours,
            ts.jobCount,
            onSiteHrs,
            driveHrs,
            late
          ];
        });

        const csvContent = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `timesheets_${dateFrom}_to_${dateTo}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Export error:', e);
        alert('Export failed: ' + e.message);
      }

      this.exporting = false;
    }
  },

  mounted() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.selectedDate = `${yyyy}-${mm}-${dd}`;
  }
};
</script>
